import { useRef } from 'react';
import { rotuloFidelidadeConta } from '@/lib/fidelidadeConta';
import { useQuery } from '@tanstack/react-query';
import { ClickToCall } from '@/components/common/ClickToCall';
import { supabase } from '@/integrations/supabase/client';
import { buscarUltimaFaturaValida, comoAFaturaFoiLida } from '@/lib/faturaValida';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Phone, TrendingUp, Calendar, Wifi, FileText, Download } from 'lucide-react';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';

// Tipo para linhas editadas - todos os campos exceto "Linha"
export interface LinhaEditada {
  // Situação atual (editável)
  plano_atual: string;
  valor_atual: number;
  gb_livre: number;
  uso_real: number;
  // Proposta (editável)
  gb_proposto: number;
  gb_redes_proposto: number;
  valor_proposto: number;
}

interface LinhasFaturaTableProps {
  clienteId?: string | null;
  cnpj?: string | null;
  tempoContratoVoz?: number | null;
  leadId?: string | null;
  isEditing?: boolean;
  editedLinhas?: Map<string, LinhaEditada>;
  onUpdateLinha?: (msisdn: string, campo: keyof LinhaEditada, valor: string | number) => void;
}

interface LinhaFatura {
  id: string;
  msisdn: string;
  plano_atual: string | null;
  valor_atual: number | null;
  gb_nominal: number | null;
  gb_bonus: number | null;
  gb_turbo: number | null;
  data_fidelidade: string | null;
}

interface LinhaAnalise {
  msisdn: string;
  gb_proposto: number;
  valor_proposto: number;
  gb_redes_proposto: number;
  uso_gb: number;
  tem_aparelho: boolean;
  valor_aparelho: number;
}

export function LinhasFaturaTable({ clienteId, cnpj, tempoContratoVoz, leadId, isEditing = false, editedLinhas, onUpdateLinha }: LinhasFaturaTableProps) {
  // De onde a leitura saiu. Ate 23/09/2026 a tela mostrava numero lido de uma
  // IMAGEM com a mesma cara de numero lido do documento — e na A KASPER eram
  // quatro telefones que nao existiam. Origem da leitura e' fato, nao detalhe.
  const faturaEscolhidaRef = useRef<{ leitura_metodo?: string | null } | null>(null);
  // Buscar linhas APENAS da última fatura (evita duplicação) - por CNPJ ou clienteId
  const { data: linhas, isLoading } = useQuery({
    queryKey: ['linhas-fatura', cnpj || clienteId],
    queryFn: async () => {
      // Primeiro: encontrar cliente_id pelo CNPJ se não foi passado diretamente
      let resolvedClienteId = clienteId;
      
      if (!resolvedClienteId && cnpj) {
        const { data: cliente } = await supabase
          .from('clientes')
          .select('id')
          .eq('cnpj', cnpj)
          .maybeSingle();
        
        if (!cliente) return [];
        resolvedClienteId = cliente.id;
      }
      
      if (!resolvedClienteId) return [];

      // A fatura mais recente QUE PRESTA. "mais recente por created_at" pegava
      // upload morto: 954 clientes tinham como última uma fatura com status ERRO
      // e 1.101 uma com período de rascunho. Fatura sem leitura boa não é a
      // fatura do cliente — é um arquivo que chegou.
      const latestFatura = await buscarUltimaFaturaValida(resolvedClienteId).catch(() => null);
      if (!latestFatura) return [];
      faturaEscolhidaRef.current = latestFatura as { leitura_metodo?: string | null };

      // Buscar linhas APENAS dessa fatura
      const { data, error } = await supabase
        .from('fatura_linha')
        .select(`
          id,
          msisdn,
          plano_atual,
          valor_atual,
          gb_nominal,
          gb_bonus,
          gb_turbo,
          data_fidelidade
        `)
        .eq('fatura_id', latestFatura.id);

      if (error) throw error;
      return data as LinhaFatura[];
    },
    enabled: !!(clienteId || cnpj),
  });

  // Buscar análise com propostas por linha (por CNPJ ou clienteId)
  const { data: analise } = useQuery({
    queryKey: ['fatura-analise-linhas', cnpj || clienteId],
    queryFn: async () => {
      // Primeiro: encontrar cliente_id pelo CNPJ se não foi passado diretamente
      let resolvedClienteId = clienteId;
      
      if (!resolvedClienteId && cnpj) {
        const { data: cliente } = await supabase
          .from('clientes')
          .select('id')
          .eq('cnpj', cnpj)
          .maybeSingle();
        
        if (!cliente) return null;
        resolvedClienteId = cliente.id;
      }
      
      if (!resolvedClienteId) return null;

      // Mesma régua da consulta de linhas acima: fatura lida e não duplicada.
      const { data: uploadData, error: uploadError } = await supabase
        .from('fatura_upload')
        .select('id')
        .eq('cliente_id', resolvedClienteId)
        .is('duplicada_de', null)
        .not('periodo_fim', 'is', null)
        .not('conta_numero', 'is', null)
        .gt('linhas_detectadas', 0)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (uploadError || !uploadData) return null;

      // Depois, buscar a análise pelo fatura_id
      const { data, error } = await supabase
        .from('fatura_analise')
        .select('json_resultado')
        .eq('fatura_id', uploadData.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) return null;
      
      // A MARCA DO APARELHO VEM DA BASE, não do JSON (02/09/2026). A auditoria
      // mostrou que a coluna estava inerte: 37 linhas marcadas em
      // base_fatura_linha e só 3 análises com o campo no JSON — o selo nunca
      // aparecia. A base é a fonte canônica; o JSON só tem o campo nas leituras
      // feitas depois de 01/09.
      const marcaAparelho = new Map<string, number>();
      try {
        const { data: linhasBase } = await supabase
          .from('base_fatura_linha')
          .select('msisdn, valor_aparelho, tem_aparelho')
          .eq('fatura_upload_id', uploadData.id)
          .eq('tem_aparelho', true);
        (linhasBase || []).forEach((b: any) => {
          marcaAparelho.set(String(b.msisdn || '').replace(/\D/g, ''), Number(b.valor_aparelho || 0));
        });
      } catch {
        // sem a marca a tela segue funcionando — ela é sinal, não bloqueio
      }

      const resultado = data?.json_resultado as any;
      // Caminho correto: analise_detalhada.linhas
      const linhasAnalise = resultado?.analise_detalhada?.linhas || [];
      
      // Criar mapa de MSISDN normalizado → proposta
      const propostasMap = new Map<string, LinhaAnalise>();
      linhasAnalise.forEach((l: any) => {
        if (l.msisdn) {
          // Normalizar MSISDN para apenas dígitos
          const msisdnNormalizado = l.msisdn.replace(/\D/g, '');
          propostasMap.set(msisdnNormalizado, {
            msisdn: l.msisdn,
            gb_proposto: l.proposta_otimizacao?.gb_livre || 0,
            valor_proposto: l.proposta_otimizacao?.valor_mensal || 0,
            gb_redes_proposto: l.proposta_otimizacao?.gb_redes_sociais || 0,
            uso_gb: l.situacao_atual?.uso_gb || 0,
            // Douglas 01/09/2026: linha com aparelho tem que ser SINALIZADA. O
            // parcelamento é adicional da conta, não é plano da linha — e por
            // isso ela fica fora da regra de faixa da renovação.
            // O JSON só tem o campo nas análises feitas DEPOIS do deploy de 01/09;
            // a fonte que vale para o resto é a coluna da base, lida abaixo.
            tem_aparelho: !!l.situacao_atual?.tem_aparelho || marcaAparelho.has(msisdnNormalizado),
            valor_aparelho: Number(l.situacao_atual?.valor_aparelho || 0)
              || (marcaAparelho.get(msisdnNormalizado) ?? 0),
          });
        }
      });
      
      return propostasMap;
    },
    enabled: !!(clienteId || cnpj),
  });

  // Função para obter proposta da linha (normaliza MSISDN antes de buscar)
  const getPropostaLinha = (msisdn: string): LinhaAnalise | undefined => {
    if (!analise) return undefined;
    const msisdnNormalizado = msisdn.replace(/\D/g, '');
    return analise.get(msisdnNormalizado);
  };

  if (!clienteId && !cnpj) return null;

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Linhas da Fatura
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!linhas?.length) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Linhas da Fatura
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            Nenhuma fatura processada para este cliente. Solicite uma fatura para ver as linhas.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Calcular totais - considerar valores editados quando em modo de edição
  const totais = linhas.reduce((acc, linha) => {
    const msisdnNorm = linha.msisdn.replace(/\D/g, '');
    const editedLinha = editedLinhas?.get(msisdnNorm);
    const proposta = getPropostaLinha(linha.msisdn);
    
    // Usar valores editados se disponíveis, senão usar originais
    const valorAtual = isEditing && editedLinha?.valor_atual !== undefined
      ? editedLinha.valor_atual
      : (linha.valor_atual || 0);
    const gbLivre = isEditing && editedLinha?.gb_livre !== undefined
      ? editedLinha.gb_livre
      : ((linha.gb_nominal || 0) + (linha.gb_bonus || 0));
    const gbProposto = editedLinha?.gb_proposto ?? proposta?.gb_proposto ?? 0;
    const gbRedesProposto = editedLinha?.gb_redes_proposto ?? proposta?.gb_redes_proposto ?? 0;
    const valorProposto = editedLinha?.valor_proposto ?? proposta?.valor_proposto ?? 0;
    
    return {
      qtd: acc.qtd + 1,
      valor: acc.valor + valorAtual,
      gbTotal: acc.gbTotal + gbLivre,
      gbRedes: acc.gbRedes + (linha.gb_turbo || 0),
      gbProposto: acc.gbProposto + gbProposto,
      valorProposto: acc.valorProposto + valorProposto,
      gbRedesProposto: acc.gbRedesProposto + gbRedesProposto,
    };
  }, { qtd: 0, valor: 0, gbTotal: 0, gbRedes: 0, gbProposto: 0, valorProposto: 0, gbRedesProposto: 0 });

  // Maior data de fidelidade
  const maiorFidelidade = linhas.reduce((maior, linha) => {
    if (!linha.data_fidelidade) return maior;
    if (!maior) return linha.data_fidelidade;
    return new Date(linha.data_fidelidade) > new Date(maior) ? linha.data_fidelidade : maior;
  }, null as string | null);

  const formatMsisdn = (msisdn: string) => {
    if (!msisdn) return '—';
    const clean = msisdn.replace(/\D/g, '');
    // Formato completo: (XX) XXXXX-XXXX
    if (clean.length === 11) {
      return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
    }
    if (clean.length === 10) {
      return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`;
    }
    return msisdn;
  };

  const formatCurrency = (value: number | null) => {
    if (value === null || value === undefined) return '—';
    return new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    }).format(value);
  };

  const formatGB = (gb: number | null) => {
    if (gb === null || gb === undefined || gb === 0) return '—';
    return `${gb} GB`;
  };

  const formatFidelidade = (data: string | null) => {
    if (!data) return 'Sem fidelidade';
    try {
      return format(new Date(data), 'dd/MM/yyyy');
    } catch {
      return data;
    }
  };

  const formatContrato = (meses: number | null | undefined) => {
    if (!meses) return '—';
    return `M${meses}`;
  };

  // Handler para download da grade
  const handleDownloadGrade = () => {
    if (!linhas?.length) return;

    const dados = linhas.map(linha => {
      const proposta = getPropostaLinha(linha.msisdn);
      return {
        'Linha': formatMsisdn(linha.msisdn),
        'Plano Atual': linha.plano_atual || '',
        'Valor Atual (R$)': linha.valor_atual || 0,
        'GB Livre': (linha.gb_nominal || 0) + (linha.gb_bonus || 0),
        'Uso Real (GB)': proposta?.uso_gb || 0,
        'Fidelidade': formatFidelidade(linha.data_fidelidade),
        'GB Proposto': (proposta?.gb_proposto || 0) + (proposta?.gb_redes_proposto || 0),
        'Valor Proposto (R$)': proposta?.valor_proposto || 0,
      };
    });

    // Adicionar linha de totais
    dados.push({
      'Linha': 'TOTAL',
      'Plano Atual': `${totais.qtd} linhas`,
      'Valor Atual (R$)': totais.valor,
      'GB Livre': totais.gbTotal,
      'Uso Real (GB)': 0,
      'GB Proposto': totais.gbProposto + totais.gbRedesProposto,
      'Valor Proposto (R$)': totais.valorProposto,
    });

    const ws = XLSX.utils.json_to_sheet(dados);
    
    // Ajustar largura das colunas
    ws['!cols'] = [
      { wch: 18 }, // Linha
      { wch: 40 }, // Plano Atual
      { wch: 14 }, // Valor Atual
      { wch: 10 }, // GB Livre
      { wch: 12 }, // Uso Real
      { wch: 12 }, // GB Proposto
      { wch: 16 }, // Valor Proposto
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Linhas');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const cnpjFormatado = cnpj?.replace(/\D/g, '') || 'cliente';
    saveAs(blob, `grade_linhas_${cnpjFormatado}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);

    toast.success('Grade baixada com sucesso!');
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Linhas da Última Fatura Analisada
          </CardTitle>
          <Button variant="outline" size="sm" onClick={handleDownloadGrade}>
            <Download className="h-4 w-4 mr-2" />
            Baixar Grade
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {/* Badges de resumo */}
        <div className="flex flex-wrap items-center gap-2 px-4 pb-3 border-b">
          {/* Badges em linha única */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
              {totais.qtd} {totais.qtd === 1 ? 'linha' : 'linhas'}
            </Badge>
            {tempoContratoVoz && tempoContratoVoz > 0 && (
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                <FileText className="h-3 w-3 mr-1" />
                M{tempoContratoVoz}
              </Badge>
            )}
            <Badge variant="outline" className="bg-muted-foreground/10 text-muted-foreground border-border">
              <Wifi className="h-3 w-3 mr-1" />
              {totais.gbTotal} GB livres
            </Badge>
            <Badge variant="outline" className="bg-muted-foreground/10 text-muted-foreground border-border">
              <TrendingUp className="h-3 w-3 mr-1" />
              {formatCurrency(totais.valor)}
            </Badge>
            {maiorFidelidade && (
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                <Calendar className="h-3 w-3 mr-1" />
                Fidelidade até: {formatFidelidade(maiorFidelidade)}
              </Badge>
            )}
            {(() => {
              const lida = comoAFaturaFoiLida(faturaEscolhidaRef.current?.leitura_metodo);
              if (!lida || lida.rotulo === 'Lida do documento') return null;
              return (
                <Badge
                  variant="outline"
                  className="bg-primary/10 text-primary border-primary/30"
                  title={lida.detalhe}
                >
                  <FileText className="h-3 w-3 mr-1" />
                  {lida.rotulo}
                </Badge>
              );
            })()}

            {/* Separador e proposta */}
            {(totais.gbProposto > 0 || totais.valorProposto > 0) && (
              <>
                <span className="text-muted-foreground mx-1">→</span>
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                  <Wifi className="h-3 w-3 mr-1" />
                  {totais.gbProposto + totais.gbRedesProposto} GB
                </Badge>
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  {formatCurrency(totais.valorProposto)}
                </Badge>
                {totais.valor - totais.valorProposto > 0 && (
                  <Badge className="bg-primary text-white border-primary">
                    Economia: {formatCurrency(totais.valor - totais.valorProposto)}/mês
                  </Badge>
                )}
              </>
            )}
          </div>
        </div>

        {/* Tabela */}
        <div className="overflow-x-auto">
          <Table noWrapper>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs font-medium">Linha</TableHead>
                <TableHead className="text-xs font-medium">Plano</TableHead>
                <TableHead className="text-xs font-medium text-right">Valor</TableHead>
                <TableHead className="text-xs font-medium text-right">GB Livre</TableHead>
                <TableHead className="text-xs font-medium text-right text-primary">Uso Real</TableHead>
                <TableHead className="text-xs font-medium text-center">Fidelidade</TableHead>
                <TableHead className="text-xs font-medium text-right text-primary">GB Prop.</TableHead>
                <TableHead className="text-xs font-medium text-right text-primary">Valor Prop.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {linhas.map((linha) => {
                const gbLivreOriginal = (linha.gb_nominal || 0) + (linha.gb_bonus || 0);
                const proposta = getPropostaLinha(linha.msisdn);
                const msisdnNorm = linha.msisdn.replace(/\D/g, '');
                
                // Obter valores editados ou originais
                const editedLinha = editedLinhas?.get(msisdnNorm);
                
                // Valores da situação atual (editáveis)
                const planoValue = editedLinha?.plano_atual ?? linha.plano_atual ?? '';
                const valorAtualValue = editedLinha?.valor_atual ?? linha.valor_atual ?? 0;
                const gbLivreValue = editedLinha?.gb_livre ?? gbLivreOriginal;
                const usoRealValue = editedLinha?.uso_real ?? proposta?.uso_gb ?? 0;
                
                // Valores da proposta (editáveis)
                const gbPropostoValue = editedLinha?.gb_proposto ?? proposta?.gb_proposto ?? 0;
                const gbRedesPropostoValue = editedLinha?.gb_redes_proposto ?? proposta?.gb_redes_proposto ?? 0;
                const valorPropostoValue = editedLinha?.valor_proposto ?? proposta?.valor_proposto ?? 0;
                
                return (
                  <TableRow key={linha.id} className="text-sm">
                    {/* Linha (MSISDN) - NÃO EDITÁVEL */}
                    <TableCell className="font-mono text-xs py-2">
                      <ClickToCall 
                        telefone={linha.msisdn} 
                        leadId={leadId || undefined}
                        className="flex items-center gap-1.5 text-primary hover:text-primary/80 hover:underline cursor-pointer transition-colors"
                      >
                        <Phone className="h-3 w-3" />
                        {formatMsisdn(linha.msisdn)}
                      </ClickToCall>
                      {proposta?.tem_aparelho && (
                        <span
                          className="mt-1 inline-flex items-center rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                          title={`Parcelamento de aparelho de ${formatCurrency(proposta.valor_aparelho)} — é adicional da conta, não é plano da linha. Por isso esta linha fica fora da regra de faixa.`}
                        >
                          Aparelho {formatCurrency(proposta.valor_aparelho)}
                        </span>
                      )}
                    </TableCell>
                    
                    {/* Plano - EDITÁVEL */}
                    <TableCell className="py-2 max-w-[150px]">
                      {isEditing ? (
                        <Input
                          type="text"
                          className="w-36 h-7 text-xs border-warning bg-warning/5"
                          value={planoValue}
                          onChange={(e) => onUpdateLinha?.(msisdnNorm, 'plano_atual', e.target.value)}
                        />
                      ) : (
                        <span className="truncate" title={linha.plano_atual || ''}>
                          {linha.plano_atual || '—'}
                        </span>
                      )}
                    </TableCell>
                    
                    {/* Valor Atual - EDITÁVEL */}
                    <TableCell className="text-right py-2 font-medium">
                      {isEditing ? (
                        <Input
                          type="number"
                          step={0.01}
                          min={0}
                          className="w-24 h-7 text-right text-xs font-medium border-warning bg-warning/5"
                          value={valorAtualValue || ''}
                          onChange={(e) => onUpdateLinha?.(msisdnNorm, 'valor_atual', Number(e.target.value) || 0)}
                        />
                      ) : (
                        formatCurrency(linha.valor_atual)
                      )}
                    </TableCell>
                    
                    {/* GB Livre - EDITÁVEL */}
                    <TableCell className="text-right py-2">
                      {isEditing ? (
                        <Input
                          type="number"
                          min={0}
                          className="w-16 h-7 text-right text-xs border-warning bg-warning/5"
                          value={gbLivreValue || ''}
                          onChange={(e) => onUpdateLinha?.(msisdnNorm, 'gb_livre', Number(e.target.value) || 0)}
                        />
                      ) : (
                        formatGB(gbLivreOriginal)
                      )}
                    </TableCell>
                    
                    {/* Uso Real - EDITÁVEL */}
                    <TableCell className="text-right py-2 text-primary font-medium">
                      {isEditing ? (
                        <Input
                          type="number"
                          step={0.1}
                          min={0}
                          className="w-16 h-7 text-right text-xs text-primary font-medium border-warning bg-warning/5"
                          value={usoRealValue || ''}
                          onChange={(e) => onUpdateLinha?.(msisdnNorm, 'uso_real', Number(e.target.value) || 0)}
                        />
                      ) : (
                        proposta !== undefined ? `${(proposta.uso_gb || 0).toFixed(1)} GB` : '—'
                      )}
                    </TableCell>
                    
                    {/* Fidelidade (situação atual da linha, não editável) */}
                    <TableCell className="text-center py-2 text-xs">
                      {linha.data_fidelidade
                        ? <span className="font-medium text-foreground">{rotuloFidelidadeConta(linha.data_fidelidade)}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>

                    {/* GB Proposto (inclui redes) */}
                    <TableCell className="text-right py-2 text-primary font-medium">
                      {isEditing ? (
                        <Input
                          type="number"
                          min={0}
                          className="w-20 h-7 text-right text-primary font-medium border-warning bg-warning/5"
                          value={(gbPropostoValue + gbRedesPropostoValue) || ''}
                          onChange={(e) => {
                            const novoTotal = Number(e.target.value) || 0;
                            onUpdateLinha?.(msisdnNorm, 'gb_proposto', novoTotal);
                            onUpdateLinha?.(msisdnNorm, 'gb_redes_proposto', 0);
                          }}
                        />
                      ) : (
                        (gbPropostoValue + gbRedesPropostoValue) > 0 ? formatGB(gbPropostoValue + gbRedesPropostoValue) : '—'
                      )}
                    </TableCell>
                    
                    {/* Valor Proposto - Editável */}
                    <TableCell className="text-right py-2 text-primary font-medium">
                      {isEditing ? (
                        <Input
                          type="number"
                          min={0}
                          step={0.01}
                          className="w-24 h-7 text-right text-primary font-medium border-warning bg-warning/5"
                          value={valorPropostoValue || ''}
                          onChange={(e) => onUpdateLinha?.(msisdnNorm, 'valor_proposto', Number(e.target.value) || 0)}
                        />
                      ) : (
                        valorPropostoValue ? formatCurrency(valorPropostoValue) : '—'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
